import { useEffect, useState } from "react";
import { devicesService } from "@/services";
import { formatDateTime } from "@/utils/format";

interface PendingDeviceDetails {
  ipAddress?: string;
  requestedAt?: string;
  browser?: string;
  os?: string;
}

interface PendingApprovalViewProps {
  email: string;
  password?: string;
  details: PendingDeviceDetails;
  onBackToLogin: () => void;
  onApproved: () => void;
}

export function PendingApprovalView({
  email,
  details,
  onBackToLogin,
  onApproved,
}: PendingApprovalViewProps) {
  const [checking, setChecking] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const checkStatus = async () => {
    setChecking(true);
    setStatusMessage(null);
    try {
      const res = await devicesService.checkDeviceStatus(email, details.ipAddress);
      if (res.status === "APPROVED") {
        onApproved();
      } else if (res.status === "REJECTED" || res.status === "REVOKED") {
        setStatusMessage(
          `IP address was ${res.status.toLowerCase()} by an administrator.${
            res.rejectReason ? ` Reason: ${res.rejectReason}` : ""
          }`
        );
      } else {
        setStatusMessage("Still awaiting administrator approval. Please check again shortly.");
      }
    } catch {
      setStatusMessage("Could not verify status. Please check your network connection.");
    } finally {
      setChecking(false);
    }
  };

  // Auto-poll approval status every 6 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      devicesService
        .checkDeviceStatus(email, details.ipAddress)
        .then((res) => {
          if (res.status === "APPROVED") {
            onApproved();
          }
        })
        .catch(() => {});
    }, 6000);

    return () => clearInterval(timer);
  }, [details.ipAddress, email, onApproved]);

  const copyIp = () => {
    if (!details.ipAddress) return;
    navigator.clipboard.writeText(details.ipAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-warning-100 text-warning-700">
          <i className="ri-router-line text-2xl" />
        </div>
        <h2 className="mt-3 text-lg font-semibold text-foreground-900">IP Approval Required</h2>
        <p className="mt-1 text-xs text-foreground-500">
          For financial security, access is restricted to verified IP addresses. Maximum 1 active IP permitted per user.
        </p>
      </div>

      <div className="rounded-lg border border-warning-200 bg-warning-50/60 p-4 text-xs text-warning-800">
        <div className="flex items-start gap-2.5">
          <i className="ri-information-line mt-0.5 flex-none text-base text-warning-700" />
          <div>
            <span className="font-medium text-warning-900">Approval Request Submitted</span>
            <p className="mt-0.5 text-warning-700">
              An administrator has been notified. Please share your System IP Address if required for verification.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3 rounded-lg border border-background-200 bg-background-50 p-4 text-xs">
        <div className="flex items-center justify-between border-b border-background-200 pb-2">
          <span className="text-foreground-500">Approval Status</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-warning-100 px-2.5 py-0.5 font-medium text-warning-800">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-warning-500" />
            Pending Approval
          </span>
        </div>

        <div className="flex items-center justify-between border-b border-background-200 pb-2">
          <span className="text-foreground-500">Browser & OS</span>
          <span className="font-medium text-foreground-800">
            {details.browser || "Browser"} on {details.os || "Device"}
          </span>
        </div>

        {details.requestedAt ? (
          <div className="flex items-center justify-between border-b border-background-200 pb-2">
            <span className="text-foreground-500">Requested On</span>
            <span className="font-medium text-foreground-700">
              {formatDateTime(details.requestedAt)}
            </span>
          </div>
        ) : null}

        <div className="space-y-1 pt-1">
          <div className="flex items-center justify-between">
            <span className="text-foreground-500">System IP Address</span>
            {details.ipAddress ? (
              <button
                type="button"
                onClick={copyIp}
                className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline"
              >
                {copied ? "Copied!" : "Copy IP"}
              </button>
            ) : null}
          </div>
          <p className="break-all font-mono text-xs font-semibold text-primary-700 bg-primary-50/50 p-2.5 rounded border border-primary-200/60">
            {details.ipAddress || "Current IP"}
          </p>
        </div>
      </div>

      {statusMessage ? (
        <div className="rounded-md border border-background-200 bg-background-100 p-2.5 text-center text-xs text-foreground-700">
          {statusMessage}
        </div>
      ) : null}

      <div className="space-y-2">
        <button
          type="button"
          onClick={checkStatus}
          disabled={checking}
          className="flex h-9 w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-4 text-xs font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-60"
        >
          {checking ? (
            <>
              <i className="ri-loader-4-line animate-spin text-sm" />
              Checking with server...
            </>
          ) : (
            <>
              <i className="ri-refresh-line text-sm" />
              Check Approval Status
            </>
          )}
        </button>

        <button
          type="button"
          onClick={onBackToLogin}
          className="h-9 w-full rounded-md border border-background-200 bg-background-50 px-4 text-xs font-medium text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
        >
          Back to Sign in
        </button>
      </div>
    </div>
  );
}
